// @ts-nocheck
/**
 * Learning Progress Chart Component
 * Carbon Charts implementation for learning progress visualization over time
 */

import React, { useMemo } from 'react';
import { LineChart } from '@carbon/charts-react';
import { Tile, Loading, InlineNotification } from '@carbon/react';
import { ChartLine } from '@carbon/icons-react';
import '@carbon/charts-react/styles.css';

interface LearningProgressData {
  date: string;
  mastery: number;
  engagement: number;
  student: string;
}

interface LearningProgressChartProps {
  data: LearningProgressData[];
  height?: number;
  title?: string;
  loading?: boolean;
  error?: string;
}

const LearningProgressChart: React.FC<LearningProgressChartProps> = ({
  data,
  height = 300,
  title = 'Learning Progress Over Time',
  loading = false,
  error = '',
}) => {
  // Transform data for Carbon Charts format
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Transform to Carbon Charts format with multiple series
    const result: any[] = [];

    // Group by student for multiple lines
    const studentGroups: { [key: string]: LearningProgressData[] } = {};
    data.forEach(item => {
      if (!studentGroups[item.student]) {
        studentGroups[item.student] = [];
      }
      studentGroups[item.student].push(item);
    });

    // Create data points for each student's mastery level
    Object.keys(studentGroups).forEach(student => {
      studentGroups[student].forEach(item => {
        result.push({
          date: item.date,
          group: `${student} - Mastery`,
          value: item.mastery,
        });
      });
    });

    return result;
  }, [data]);

  // Carbon Charts options with Sigma08 theme colors
  const chartOptions = useMemo(
    () => ({
      title: '',
      axes: {
        left: {
          title: 'Mastery Level (%)',
          mapsTo: 'value',
          domain: [0, 100],
        },
        bottom: {
          title: 'Date',
          mapsTo: 'date',
          scaleType: 'time' as const,
        },
      },
      curve: 'curveMonotoneX' as const,
      height,
      theme: 'white',
      color: {
        scale: {
          'Student - Mastery': '#8a3ffc', // Galaxy Purple
          'Average - Mastery': '#0043ce', // Carbon Blue
          default: '#525252', // Carbon Gray
        },
      },
      grid: {
        x: {
          enabled: false,
        },
        y: {
          enabled: true,
        },
      },
      points: {
        enabled: true,
        radius: 4,
        fillOpacity: 1,
        filled: true,
      },
      tooltip: {
        enabled: true,
        customHTML: (datapoints: any[]) => {
          if (!datapoints || datapoints.length === 0) return '';

          const point = datapoints[0];
          const originalData = data.find(d => d.date === point.date && point.group?.includes(d.student));

          return `
          <div class="bx--cc--tooltip">
            <p class="bx--cc--tooltip-total">${new Date(point.date).toLocaleDateString()}</p>
            <ul class="bx--cc--tooltip-data">
              <li>
                <span class="bx--cc--tooltip-data__label">Student:</span>
                <span class="bx--cc--tooltip-data__value">${point.group?.split(' - ')[0]}</span>
              </li>
              <li>
                <span class="bx--cc--tooltip-data__label">Mastery:</span>
                <span class="bx--cc--tooltip-data__value">${point.value}%</span>
              </li>
              ${
                originalData?.engagement
                  ? `
              <li>
                <span class="bx--cc--tooltip-data__label">Engagement:</span>
                <span class="bx--cc--tooltip-data__value">${originalData.engagement} min</span>
              </li>`
                  : ''
              }
            </ul>
          </div>
        `;
        },
      },
      legend: {
        enabled: true,
        position: 'top' as const,
        clickable: true,
      },
      animations: {
        enabled: true,
      },
      timeScale: {
        addSpaceOnEdges: 1,
      },
    }),
    [height, data]
  );

  if (loading) {
    return (
      <Tile>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: height + 40,
            gap: 'var(--cds-spacing-03)',
          }}
        >
          <Loading withOverlay={false} />
          <span style={{ color: 'var(--cds-text-secondary)' }}>Loading progress data...</span>
        </div>
      </Tile>
    );
  }

  if (error) {
    return (
      <Tile>
        <InlineNotification title="Chart Error" subtitle={error} kind="error" lowContrast hideCloseButton />
      </Tile>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Tile>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: height + 40,
            color: 'var(--cds-text-secondary)',
          }}
        >
          <ChartLine size={32} style={{ marginBottom: 'var(--cds-spacing-03)' }} />
          <span>No progress data available</span>
        </div>
      </Tile>
    );
  }

  return (
    <Tile>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--cds-spacing-03)',
          marginBottom: 'var(--cds-spacing-05)',
        }}
      >
        <ChartLine size={20} style={{ color: 'var(--cds-interactive-01)' }} />
        <h4
          style={{
            fontSize: 'var(--cds-productive-heading-03-font-size)',
            fontWeight: 'var(--cds-productive-heading-03-font-weight)',
            lineHeight: 'var(--cds-productive-heading-03-line-height)',
            letterSpacing: 'var(--cds-productive-heading-03-letter-spacing)',
            margin: 0,
          }}
        >
          {title}
        </h4>
      </div>

      <div
        style={{
          backgroundColor: 'var(--cds-layer-01)',
          borderRadius: 'var(--cds-border-radius)',
          padding: 'var(--cds-spacing-03)',
        }}
      >
        <LineChart data={chartData} options={chartOptions} />
      </div>
    </Tile>
  );
};

export default LearningProgressChart;
